import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate, Outlet } from 'react-router-dom';
import toast from 'react-hot-toast';
import { config } from '../config';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import './role-navigation.css';

export default function EnseignantLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [nonLus, setNonLus] = useState(0);
  const navigate = useNavigate();
  const menuTrigger = useRef(null);
  const menuPanel = useRef(null);
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const API_BASE = config.apiBase;

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    toast.success('Déconnexion réussie');
    navigate('/login');
  };

  const checkMessages = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const response = await fetch(`${API_BASE}/get_messages_techniques.php`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setNonLus(data.non_lus);
      }
    } catch (error) {
      console.error('Erreur chargement messages:', error);
    }
  };

  useEffect(() => {
    checkMessages();
    const interval = setInterval(checkMessages, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!sidebarOpen) return undefined;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const panel = menuPanel.current;
    panel?.querySelector('button')?.focus();
    const key = event => {
      if (event.key === 'Escape') setSidebarOpen(false);
      if (event.key === 'Tab') {
        const items = panel?.querySelectorAll('button, a[href]');
        if (!items?.length) return;
        if (event.shiftKey && document.activeElement === items[0]) { event.preventDefault(); items[items.length-1].focus(); }
        else if (!event.shiftKey && document.activeElement === items[items.length-1]) { event.preventDefault(); items[0].focus(); }
      }
    };
    const resize = () => { if (window.innerWidth >= 1024) setSidebarOpen(false); };
    window.addEventListener('keydown', key); window.addEventListener('resize', resize);
    return () => { document.body.style.overflow = overflow; window.removeEventListener('keydown', key); window.removeEventListener('resize', resize); menuTrigger.current?.focus(); };
  }, [sidebarOpen]);

  const menuItems = [
    { path: '/securite', name: 'Connexion & empreinte', icon: '↗' },
    { path: '/enseignant/dashboard', name: 'Tableau de bord', icon: '📊' },
    { path: '/tv-sahel', name: 'TV Sahel', icon: '📺' },
    { path: '/enseignant/cours', name: 'Mes cours', icon: '📚' },
    { path: '/enseignant/messagerie', name: 'Messagerie', icon: '💬' },
    { path: '/enseignant/statistiques', name: 'Statistiques', icon: '📈' },
    { path: '/enseignant/feed', name: "Actualités", icon: '✨' },
    // Dans EnseignantLayout.js
{ path: '/enseignant/formations', name: 'Formations', icon: '🎓' },
    { path: '/enseignant/mes-medias', name: 'Mes médias', icon: '📱' },
    { path: '/enseignant/profile', name: 'Mon profil', icon: '👤' },
    { path: '/messages-techniques', name: 'Messages techniques', icon: '💬' },
  ];

  return (
    <>
      {/* Sidebar desktop */}
      <div className="teacher-sidebar hidden lg:flex lg:flex-col lg:w-64 lg:fixed lg:left-0 lg:top-0 lg:h-full lg:bg-gradient-to-b lg:from-blue-800 lg:to-blue-950 lg:text-white lg:shadow-xl">
        <div className="flex flex-col h-full">
          <div className="flex-shrink-0 p-5 border-b border-blue-700/50">
            <div className="text-center">
              <div className="w-14 h-14 bg-blue-600/30 rounded-full flex items-center justify-center mx-auto mb-2">
                <span className="text-2xl">👨‍🏫</span>
              </div>
              <h2 className="text-lg font-bold">Espace Enseignant</h2>
              <p className="text-xs text-blue-300 mt-0.5">{user.prenom} {user.nom}</p>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto py-3 px-3 space-y-1">
            {menuItems.map((item) => (
              <NavLink 
                key={item.path} 
                to={item.path} 
                className={({ isActive }) => 
                  `flex items-center gap-3 px-3 py-2 rounded-lg transition text-sm relative ${
                    isActive 
                      ? 'bg-blue-600 text-white' 
                      : 'hover:bg-blue-700/50 text-blue-100'
                  }`
                }
              >
                <span className="text-lg">{item.icon}</span>
                <span className="flex-1">{item.name}</span>
                {item.path === '/messages-techniques' && nonLus > 0 && (
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs font-bold text-white animate-pulse">
                    {nonLus > 9 ? '9+' : nonLus}
                  </span>
                )}
              </NavLink>
            ))}
          </div>
          <div className="flex-shrink-0 p-3 border-t border-blue-700/50">
            <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-red-600/30 text-blue-100 transition text-sm">
              <span className="text-lg">🚪</span>
              <span>Déconnexion</span>
            </button>
          </div>
        </div>
      </div>

      <header className="teacher-mobile-header">
        <button ref={menuTrigger} type="button" onClick={() => setSidebarOpen(true)} className="role-menu-trigger" aria-label="Ouvrir le menu enseignant" aria-expanded={sidebarOpen} aria-controls="teacher-mobile-menu"><Bars3Icon /></button>
        <span>Espace enseignant</span><span className="teacher-brand">IPTIC-Golkoye</span>
      </header>

      {/* Sidebar mobile */}
      {sidebarOpen && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
          <div ref={menuPanel} id="teacher-mobile-menu" role="dialog" aria-modal="true" aria-label="Menu enseignant" className="teacher-sidebar fixed top-0 left-0 h-full w-64 bg-gradient-to-b from-blue-800 to-blue-950 text-white z-50">
            <div className="flex flex-col h-full">
              <div className="flex-shrink-0 p-5 border-b border-blue-700/50">
                <div className="flex justify-between items-center">
                  <div className="text-center flex-1">
                    <div className="w-12 h-12 bg-blue-600/30 rounded-full flex items-center justify-center mx-auto mb-2">
                      <span className="text-2xl">👨‍🏫</span>
                    </div>
                    <h2 className="text-lg font-bold">Espace Enseignant</h2>
                    <p className="text-xs text-blue-300 mt-0.5">{user.prenom} {user.nom}</p>
                  </div>
                  <button type="button" aria-label="Fermer le menu enseignant" onClick={() => setSidebarOpen(false)} className="role-menu-trigger"><XMarkIcon /></button>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto py-3 px-3 space-y-1">
                {menuItems.map((item) => (
                  <NavLink 
                    key={item.path} 
                    to={item.path} 
                    onClick={() => setSidebarOpen(false)} 
                    className={({ isActive }) => 
                      `flex items-center gap-3 px-3 py-2 rounded-lg transition text-sm relative ${
                        isActive 
                          ? 'bg-blue-600 text-white' 
                          : 'hover:bg-blue-700/50 text-blue-100'
                      }`
                    }
                  >
                    <span className="text-lg">{item.icon}</span>
                    <span className="flex-1">{item.name}</span>
                    {item.path === '/messages-techniques' && nonLus > 0 && (
                      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs font-bold">
                        {nonLus > 9 ? '9+' : nonLus}
                      </span>
                    )}
                  </NavLink>
                ))}
              </div>
              <div className="flex-shrink-0 p-3 border-t border-blue-700/50">
                <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-red-600/30 text-blue-100 transition text-sm">
                  <span className="text-lg">🚪</span>
                  <span>Déconnexion</span>
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      <div className="lg:ml-64">
        <Outlet />
      </div>
    </>
  );
}
